import { z } from 'zod'

import { AnalysisStatusEnum } from './asset-analysis.ts'
import { CampaignSchema } from './campaign.ts'
import { idSchema, MutationIdSchema, msTimestampSchema } from './common.ts'

// 'uploading' is new for PR 4's client-driven upload flow: the row is created before the client
// finishes the storage PUT, while 'awaiting_acceptance' holds completed uploads for creator review.
// 'spent' and 'blocked' are RAL-108's reject-reason outcomes — deliberately distinct from 'held'
// (which means "has an undecided proposal in flight", see the Asset.status writes in
// backend/services/agent/agent.ts and backend/routes/api/proposals.ts's rewrite route). Reusing
// 'held' for either would make a permanently-retired clip indistinguishable from one just
// awaiting a decision. Both are terminal for selectCandidates() (rules.ts only pulls
// 'inventory'), so no agent-loop change is needed to keep them out of the shortlist.
export const AssetStatusEnum = z.enum([
  'inventory',
  'held',
  'rejected',
  'uploading',
  'awaiting_acceptance',
  'spent',
  'blocked',
  'reading',
  'held_back'
])
export type AssetStatus = z.infer<typeof AssetStatusEnum>

export const AssetTypeEnum = z.enum(['Hook', 'Crowd', 'Behind the scenes', 'UGC'])
export type AssetType = z.infer<typeof AssetTypeEnum>

export const AssetRightsEnum = z.enum(['Owned', 'Licensed', 'Unconfirmed'])
export type AssetRights = z.infer<typeof AssetRightsEnum>

export const CampaignIdsSchema = z.array(idSchema).max(50)
export type CampaignIds = z.infer<typeof CampaignIdsSchema>

export const SidecarHintsSchema = z.record(z.string(), z.json())

export const CompleteCreatorMetadataSchema = z
  .object({
    hook: z.string().trim().min(1).nullable(),
    type: AssetTypeEnum.nullable(),
    rights: AssetRightsEnum,
    caption: z.string().trim().min(1).nullable(),
    wantsHashtags: z.boolean(),
    campaignIds: CampaignIdsSchema,
    sidecarNote: z.string().nullable(),
    sidecarHints: SidecarHintsSchema
  })
  .strict()
export type CompleteCreatorMetadata = z.infer<typeof CompleteCreatorMetadataSchema>

export const CreatorSaveProtocolEnum = z.enum(['creator_save_v1'])
export type CreatorSaveProtocol = z.infer<typeof CreatorSaveProtocolEnum>

export const FinalizeUploadSchema = z
  .object({
    protocol: CreatorSaveProtocolEnum,
    durationSeconds: z.number().nonnegative().optional(),
    thumbnailUploaded: z.boolean().optional(),
    creatorMetadata: CompleteCreatorMetadataSchema
  })
  .strict()
export type FinalizeUpload = z.infer<typeof FinalizeUploadSchema>

export const InitUploadResponseSchema = z.object({
  assetId: idSchema,
  storageKey: z.string().min(1),
  uploadUrl: z.string().url(),
  uploadUrlExpiresAt: msTimestampSchema
})
export type InitUploadResponse = z.infer<typeof InitUploadResponseSchema>

export const AnalysisUploadResponseSchema = z.object({
  uploadUrl: z.string().url(),
  uploadUrlExpiresAt: msTimestampSchema,
  fields: z.record(z.string(), z.string())
})
export type AnalysisUploadResponse = z.infer<typeof AnalysisUploadResponseSchema>

export const ThumbnailUploadUrlResponseSchema = z.object({
  uploadUrl: z.string().url(),
  uploadUrlExpiresAt: msTimestampSchema
})
export type ThumbnailUploadUrlResponse = z.infer<typeof ThumbnailUploadUrlResponseSchema>

export const AssetSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  storageKey: z.string().min(1),
  originalFilename: z.string().min(1),
  sidecarNote: CompleteCreatorMetadataSchema.shape.sidecarNote.default(null),
  sidecarHints: CompleteCreatorMetadataSchema.shape.sidecarHints.default({}),
  // Creator-editable label fields collected by the ingest modal (RAL-43), persisted at
  // Accept time via PATCH /api/assets/:id (RAL-44) — never agent-derived.
  hook: CompleteCreatorMetadataSchema.shape.hook.default(null),
  type: CompleteCreatorMetadataSchema.shape.type.default(null),
  rights: CompleteCreatorMetadataSchema.shape.rights.default(AssetRightsEnum.enum.Unconfirmed),
  // Set once at ingest; reused verbatim across every proposal/repost of this asset while it's
  // set (RAL-114). wantsHashtags only has a visible effect when caption is null — an
  // agent-generated caption's hashtags get dropped if false. Once a user writes their own
  // caption, that text is the sole source of truth for whatever hashtags it does or doesn't
  // contain; see backend/services/agent/caption-override.ts.
  caption: CompleteCreatorMetadataSchema.shape.caption.default(null),
  wantsHashtags: CompleteCreatorMetadataSchema.shape.wantsHashtags.default(true),
  durationSeconds: z.number().nonnegative().nullable().default(null),
  // Best-effort poster frame captured client-side at upload time (RAL-170) — never blocks
  // finalize when missing. Points into the videos bucket's thumbnail/ prefix, distinct from
  // storageKey's asset/ prefix so it can never collide with the write:asset/* storage listener.
  thumbnailKey: z.string().nullable().default(null),
  // Workspace campaigns this clip is reserved for. Empty means untagged, which any campaign with
  // includeUntaggedAssets draws from (see isEligibleForCampaign in backend/models/asset.ts).
  campaignIds: CampaignIdsSchema.default([]),
  sizeBytes: z.number().nonnegative(),
  status: AssetStatusEnum.default('inventory'),
  analysisStatus: AnalysisStatusEnum.default('pending'),
  addedAt: msTimestampSchema,
  // Tier-3 delete (RAL-108): an asset with a published delivery can't be fully removed, since the
  // publish record and metric snapshots must survive (RAL-60). This hides it from the library instead.
  removedFromLibrary: z.boolean().default(false)
})

export type AssetRow = z.infer<typeof AssetSchema>

export const FinalizeUploadResponseSchema = z.object({ asset: AssetSchema })
export type FinalizeUploadResponse = z.infer<typeof FinalizeUploadResponseSchema>

export const UpdateAssetMetadataRequestSchema = z
  .object({
    hook: z.string().trim().min(1).nullable().optional(),
    type: AssetTypeEnum.nullable().optional(),
    rights: AssetRightsEnum.optional(),
    caption: z.string().trim().min(1).nullable().optional(),
    wantsHashtags: z.boolean().optional(),
    mutationId: MutationIdSchema
  })
  .strict()
export type UpdateAssetMetadataRequest = z.infer<typeof UpdateAssetMetadataRequestSchema>

export const SetAssetCampaignsRequestSchema = z
  .object({ campaignIds: CampaignIdsSchema, mutationId: MutationIdSchema })
  .strict()
export type SetAssetCampaignsRequest = z.infer<typeof SetAssetCampaignsRequestSchema>

export const SetAssetCampaignsResponseSchema = z.object({ asset: AssetSchema })
export type SetAssetCampaignsResponse = z.infer<typeof SetAssetCampaignsResponseSchema>

// `campaignId` and `untagged` are mutually exclusive tag filters; neither lists the whole library.
export const ListAssetsQuerySchema = z
  .object({
    status: AssetStatusEnum.optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    campaignId: idSchema.optional(),
    untagged: z.stringbool().optional()
  })
  .refine((query) => !(query.campaignId && query.untagged), {
    message: 'campaignId and untagged cannot be combined',
    path: ['untagged']
  })
export type ListAssetsQuery = z.infer<typeof ListAssetsQuerySchema>

// RAL-229: one campaign an asset is tagged for, as the library tile popover and asset detail read it.
// A tag whose campaign was deleted is dropped rather than shown as a nameless id.
export const AssetCampaignTagSchema = CampaignSchema.pick({ id: true, name: true, status: true, endDate: true })
export type AssetCampaignTag = z.infer<typeof AssetCampaignTagSchema>

// RAL-229: how the workspace library splits across campaign tags. `eligible*` counts only clips the agent
// can draw from (inventory, still in the library), so the untagged toggle's arithmetic matches the tick.
export const AssetTagCountsSchema = z.object({
  total: z.number().int().nonnegative(),
  untagged: z.number().int().nonnegative(),
  eligibleUntagged: z.number().int().nonnegative(),
  campaigns: z.array(
    z.object({
      campaignId: idSchema,
      tagged: z.number().int().nonnegative(),
      eligibleTagged: z.number().int().nonnegative()
    })
  )
})
export type AssetTagCounts = z.infer<typeof AssetTagCountsSchema>
